/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect } from "react";
import { en } from "./translations.js";
import { LANGUAGES, detectUserLanguage, getTranslation } from "./constants.js";

// Translation context
const TranslationContext = createContext();

/**
 * Translation Provider Component
 * Provides translation context to the entire app
 *
 * Note: This file exports both a component and a hook, which is intentional
 * for the i18n context pattern. The Fast Refresh warning can be safely ignored.
 */
export const TranslationProvider = ({ children }) => {
  const [language, setLanguage] = useState("en");
  const [translations, setTranslations] = useState(en);

  // Initialize language on mount
  useEffect(() => {
    const initializeLanguage = async () => {
      const detectedLang = await detectUserLanguage();
      setLanguage(detectedLang);
      setTranslations(getTranslation(detectedLang));
    };

    initializeLanguage();
  }, []);

  // Update translations when language changes
  const changeLanguage = (newLanguage) => {
    if (!LANGUAGES[newLanguage]) {
      console.warn(`Unsupported language: ${newLanguage}`);
      return;
    }

    setLanguage(newLanguage);
    setTranslations(getTranslation(newLanguage));

    // Save to localStorage
    localStorage.setItem("gg-language", newLanguage);
  };

  // Translation function
  const t = (key, defaultValue = "") => {
    const keys = key.split(".");
    let value = translations;

    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) {
        console.warn(`Translation key not found: ${key}`);
        return defaultValue || key;
      }
    }

    return value;
  };

  const contextValue = {
    language,
    translations,
    changeLanguage,
    t,
    availableLanguages: LANGUAGES,
  };

  return (
    <TranslationContext.Provider value={contextValue}>
      {children}
    </TranslationContext.Provider>
  );
};

/**
 * Hook to use translations
 * @returns {Object} Translation utilities
 */
export const useTranslation = () => {
  const context = useContext(TranslationContext);

  if (!context) {
    throw new Error("useTranslation must be used within a TranslationProvider");
  }

  return context;
};
